import { api } from '../client';

export interface DashboardMetrics {
  totalItems: number;
  totalLocations: number;
  totalSuppliers: number;
  totalPurchaseOrders: number;
  totalShipments: number;
  activeAlerts: number;
  pendingApprovals: number;
  totalInventoryValue: number;
}

export interface DashboardDistributions {
  purchaseOrders: Record<string, number>;
  shipments: Record<string, number>;
  inventory: {
    optimal: number;
    low: number;
    stockout: number;
    overstock: number;
    total: number;
  };
}

export interface DashboardData {
  metrics: DashboardMetrics;
  distributions: DashboardDistributions;
  recentActivity: {
    purchaseOrders: Array<{
      id: string;
      poNumber: string;
      currency?: string;
      status: string;
      totalAmount?: number;
      supplier?: { name: string };
      createdAt: string;
    }>;
    shipments: Array<{
      id: string;
      shipmentNumber: string;
      status: string;
      origin?: { name: string };
      destination?: { name: string };
      createdAt: string;
    }>;
    alerts: Array<{
      id: string;
      type: string;
      severity: string;
      title: string;
      createdAt: string;
    }>;
  };
}

export interface TrendData {
  date: string;
  purchaseOrders: number;
  poValue: number;
  shipments: number;
  alerts: number;
}

export interface Recommendation {
  id: string;
  type: string;
  priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  title: string;
  description: string;
  entityType: string;
  entityId: string;
  suggestedAction?: {
    action: string;
    quantity?: number;
  };
}

export const analyticsApi = {
  async getDashboard(companyId?: string) {
    const response = await api.get<DashboardData>('/analytics/dashboard', {
      params: companyId ? { companyId } : undefined,
    });
    return response.data;
  },

  async getStockDistribution(companyId?: string) {
    const response = await api.get<DashboardDistributions['inventory']>(
      '/analytics/stock-distribution',
      {
        params: companyId ? { companyId } : undefined,
      }
    );
    return response.data;
  },

  async getTrends(companyId?: string, days = 30) {
    const response = await api.get<TrendData[]>('/analytics/trends', {
      params: { companyId, days },
    });
    return response.data;
  },

  async getRecommendations(companyId?: string) {
    const response = await api.get<Recommendation[]>('/analytics/recommendations', {
      params: companyId ? { companyId } : undefined,
    });
    return response.data;
  },

  async markRecommendationActioned(id: string) {
    const response = await api.post(`/analytics/recommendations/${id}/action`);
    return response.data;
  },
};

// ==================== TMS Analytics (Track & Trace KPIs) ====================

export interface ShipmentKPIs {
  /** Null when no shipment has resolved. Not 0 — that would claim none arrived on time. */
  onTimeDeliveryRate: number | null;
  previousOnTimeRate: number | null;
  /** Null when either period has no basis. A first period has no trend. */
  onTimeTrend: number | null;

  /** Null when no shipment has a recorded transit time. */
  avgTransitDays: number | null;
  previousAvgTransitDays: number | null;
  transitTrend: number | null;

  activeExceptions: number;
  previousExceptions: number;
  exceptionTrend: number;

  totalDemurrageCharges: number;
  demurrageShipmentCount: number;
  avgDemurragePerShipment: number;

  totalShipments: number;
  /** Booked, in transit or delayed — committed and not yet finished. */
  activeCount: number;
  inTransitCount: number;
  delayedCount: number;
  deliveredCount: number;
}

export interface CarrierPerformance {
  carrierId: string;
  carrierName: string;
  totalShipments: number;
  onTimeRate: number;
  avgTransitDays: number;
  exceptionCount: number;
  totalDemurrageCharges: number;
  avgCostPerShipment: number;
  performanceScore: number;
}

export interface ExceptionSummary {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  byReason: Array<{
    reason: string;
    count: number;
    severity: string;
  }>;
  recentExceptions: Array<{
    shipmentId: string;
    shipmentNumber: string;
    reason: string;
    severity: string;
    createdAt: string;
  }>;
}

export interface KPIQueryParams {
  startDate?: string;
  endDate?: string;
  status?: string;
  mode?: string;
  carrierId?: string;
}

export interface CarrierPerformanceQueryParams {
  carrierId?: string;
  startDate?: string;
  endDate?: string;
}

export const tmsAnalyticsApi = {
  /**
   * Get shipment KPIs for dashboard with trend comparison
   */
  async getShipmentKPIs(params?: KPIQueryParams): Promise<ShipmentKPIs> {
    const response = await api.get<ShipmentKPIs>('/tms/analytics/kpis', {
      params,
    });
    return response.data;
  },

  /**
   * Get carrier performance scorecards
   */
  async getCarrierPerformance(
    params?: CarrierPerformanceQueryParams
  ): Promise<CarrierPerformance[]> {
    const response = await api.get<CarrierPerformance[]>('/tms/analytics/carrier-performance', {
      params,
    });
    return response.data;
  },

  /**
   * Get exception summary with breakdown by severity
   */
  async getExceptionSummary(): Promise<ExceptionSummary> {
    const response = await api.get<ExceptionSummary>('/tms/analytics/exceptions');
    return response.data;
  },
};
